import { FacebookOutlined, InstagramOutlined, YoutubeOutlined } from '@ant-design/icons';
import { createFileRoute } from '@tanstack/react-router';
import { useStore } from '@tanstack/react-store';
import React, { useEffect } from 'react';
import artistsData from '~/data/artists.json';
import { langStore } from '~/store/langStore';
import { usePosthogEvent } from '~/utils/posthog';

export const Route = createFileRoute('/artists/$slug')({
  component: RouteComponent,
  loader: ({ params }) => {
    if (!params.slug) {
      throw new Error('Slug is required');
    }
    return params.slug;
  },
});

function RouteComponent() {
  const { slug } = Route.useParams();
  const locale = useStore(langStore, s => s);
  const track = usePosthogEvent();
  const cdnBasePath = 'http://assets.malomfesztival.org';
  const artist = artistsData.find(a => a.Slug === slug);

  useEffect(() => {
    if (artist) {
      track('artist_detail_page_view', {
        artist: artist.Name,
        country: artist.Country,
        stage: artist.Stage,
        locale,
      });
    }
    const start = Date.now();
    return () => {
      if (artist) {
        const duration = Date.now() - start;
        track('artist_detail_time_spent', {
          artist: artist.Name,
          duration_ms: duration,
          locale,
        });
      }
    };
  }, [artist, locale, track]);

  if (!artist) {
    return <div className="text-center text-red-500">Artist not found</div>;
  }

  const imageUrl = `${cdnBasePath}${artist.Image}`;

  return (
    <div className="bg-blackpng flex min-h-screen w-full items-center justify-center bg-cover bg-no-repeat py-12 pb-24">
      <div className="flex w-[95vw] max-w-5xl flex-col gap-6 bg-[#3A3A3A]/80 p-6 shadow-lg md:flex-row md:p-10">
        <div className="flex w-full flex-shrink-0 flex-col items-center justify-center md:w-[260px]">
          <img
            src={imageUrl}
            alt={artist.Name}
            className="mb-3 w-full object-cover"
          />
        </div>
        <div className="flex flex-1 flex-col gap-2 text-white">
          <div className="flex justify-between">
            <h1 className="text-2xl font-bold lg:text-4xl">
              {artist.Name}
              {' '}
              (
              {artist.Country}
              )
            </h1>
            <div className="flex gap-3">
              {artist.Facebook && (
                <a
                  href={artist.Facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  onClick={() => track('artist_social_click', { artist: artist?.Name, platform: 'facebook', locale })}
                >
                  <FacebookOutlined className="text-orange text-xl md:text-2xl lg:text-4xl" />
                </a>
              )}
              {artist.Instagram && (
                <a
                  href={artist.Instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  onClick={() => track('artist_social_click', { artist: artist?.Name, platform: 'instagram', locale })}
                >
                  <InstagramOutlined className="text-orange text-xl md:text-2xl lg:text-4xl" />
                </a>
              )}
              {artist.Music && (
                <a
                  href={artist.Music}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  onClick={() => track('artist_social_click', { artist: artist?.Name, platform: 'youtube', locale })}
                >
                  <YoutubeOutlined className="text-orange text-xl md:text-2xl lg:text-4xl" />
                </a>
              )}
            </div>
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-6">
            <span className="text-orange text-xl font-bold">{artist.Stage}</span>
          </div>
          <div className="text-left text-sm leading-relaxed text-gray-100 md:text-base">
            {artist[`Desc-${locale}`] || ''}
          </div>
        </div>
      </div>
    </div>
  );
}
